import {
  getGlobalListUpgradesKey,
  getGlobalListUpgradeTemplatesKey,
  getListOrganizationsKey,
  globalListUpgradeTemplates,
  globalListUpgrades,
  listOrganizations,
  listAddons,
  getListAddonsKey,
  globalListUpgradeAssessments,
  getGlobalListUpgradeAssessmentsKey,
  listUpgradeAssessments,
  getListUpgradeAssessmentsKey,
  listUpgradeTemplates,
  getListUpgradeTemplatesKey,
  listUpgrades,
  getListUpgradesKey,
  listAddonInstances,
  getListAddonInstancesKey,
} from './admin';
import {
  GlobalListUpgradeAssessmentsParams,
  GlobalListUpgradeTemplatesParams,
  GlobalListUpgradesParams,
  ListAddonsParams,
  ListAddonInstancesParams,
  ListOrganizationsParams,
  ListUpgradeAssessmentsParams,
  ListUpgradeTemplatesParams,
  ListUpgradesParams,
} from './admin-models';
import { Key, SWRConfiguration } from 'swr';
import useSwrInfinite from 'swr/infinite';

export const useGlobalListUpgradesInfinite = <TError = unknown>(
  params?: GlobalListUpgradesParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof globalListUpgrades>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof globalListUpgrades>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null) return getGlobalListUpgradesKey(params);

    // add the cursor to the API endpoint
    return getGlobalListUpgradesKey({
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, GlobalListUpgradesParams]) =>
    globalListUpgrades(args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useListUpgradesInfinite = <TError = unknown>(
  accountId: string,
  params?: ListUpgradesParams,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof listUpgrades>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof listUpgrades>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null) return getListUpgradesKey(accountId, params);

    // add the cursor to the API endpoint
    return getListUpgradesKey(accountId, {
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, ListUpgradesParams]) =>
    listUpgrades(accountId, args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useListOrganizationsInfinite = <TError = unknown>(
  params?: ListOrganizationsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listOrganizations>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof listOrganizations>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null) return getListOrganizationsKey(params);

    // add the cursor to the API endpoint
    return getListOrganizationsKey({
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };
  const swrFn = (args: [string, ListOrganizationsParams]) =>
    listOrganizations({ page_size: 500, ...args[1] });

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useGlobalListUpgradeTemplatesInfinite = <TError = unknown>(
  params?: GlobalListUpgradeTemplatesParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof globalListUpgradeTemplates>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<
      ReturnType<typeof globalListUpgradeTemplates>
    > | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null)
      return getGlobalListUpgradeTemplatesKey(params);

    // add the cursor to the API endpoint
    return getGlobalListUpgradeTemplatesKey({
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, GlobalListUpgradeTemplatesParams]) =>
    globalListUpgradeTemplates(args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useListUpgradeTemplatesInfinite = <TError = unknown>(
  accountId: string,
  params?: ListUpgradeTemplatesParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listUpgradeTemplates>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof listUpgradeTemplates>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null)
      return getListUpgradeTemplatesKey(accountId, params);

    // add the cursor to the API endpoint
    return getListUpgradeTemplatesKey(accountId, {
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, ListUpgradeTemplatesParams]) =>
    listUpgradeTemplates(accountId, args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useGlobalListUpgradeAssessmentsInfinite = <TError = unknown>(
  params?: GlobalListUpgradeAssessmentsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof globalListUpgradeAssessments>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<
      ReturnType<typeof globalListUpgradeAssessments>
    > | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null)
      return getGlobalListUpgradeAssessmentsKey(params);

    // add the cursor to the API endpoint
    return getGlobalListUpgradeAssessmentsKey({
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, GlobalListUpgradeAssessmentsParams]) =>
    globalListUpgradeAssessments(args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useListUpgradeAssessmentsInfinite = <TError = unknown>(
  accountId: string,
  params?: ListUpgradeAssessmentsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listUpgradeAssessments>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof listUpgradeAssessments>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null)
      return getListUpgradeAssessmentsKey(accountId, params);

    // add the cursor to the API endpoint
    return getListUpgradeAssessmentsKey(accountId, {
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, ListUpgradeAssessmentsParams]) =>
    listUpgradeAssessments(accountId, args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useListAddonsInfinite = <TError = unknown>(
  params?: ListAddonsParams,
  options?: {
    swr?: SWRConfiguration<Awaited<ReturnType<typeof listAddons>>, TError> & {
      swrKey?: Key;
      enabled?: boolean;
    };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof listAddons>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null) return getListAddonsKey(params);

    // add the cursor to the API endpoint
    return getListAddonsKey({
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, ListAddonsParams]) => {
    return listAddons(args[1]);
  };

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};

export const useListAddonInstancesInfinite = <TError = unknown>(
  accountId: string,
  params?: ListAddonInstancesParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listAddonInstances>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
  },
) => {
  const { swr: swrOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false && !!accountId;
  const swrKey = (
    pageIndex: number,
    previousPageData: Awaited<ReturnType<typeof listAddonInstances>> | null,
  ) => {
    if (!isEnabled) return null;

    // reached the end
    if (previousPageData && !previousPageData.continuation_token) return null;

    if (previousPageData === null)
      return getListAddonInstancesKey(accountId, params);

    // add the cursor to the API endpoint
    return getListAddonInstancesKey(accountId, {
      ...params,
      continuation_token: previousPageData.continuation_token,
    });
  };

  const swrFn = (args: readonly [string, ListAddonInstancesParams]) =>
    listAddonInstances(accountId, args[1]);

  const query = useSwrInfinite<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    {
      revalidateFirstPage: false,
      persistSize: true,
      initialSize: 100000,
    },
  );

  return {
    swrKey,
    ...query,
  };
};
